import { AgGridReact } from "ag-grid-react";
import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilterCircleXmark } from "@fortawesome/free-solid-svg-icons";

import {
  DebounceContext,
  DebounceContextType,
} from "../../../context/DebounceContext";
import {
  retrieveValueFromLocalStorageObject,
  saveObjectsToLocalStorage,
} from "../../../helpers/local-storage.helper";
import { usePageableTable } from "../../../context/PageableTableContext";
import { Predicates } from "../../../libraries/predicates/predicates";
import { PageableTableActions } from "../../../states/pageable-table";
import { TablePagePersistentTypes } from "../../../types/persistent-page-filters";
import useArtworkSearch from "../../../hooks/useArtworkSearch";
import { PageableTable } from "../../../components/table";
import { Link } from "react-router-dom";
import ArtworkPictureModal from "../components/ArtworkPicutureModal";
import ReactModal from "react-modal";
import useAxios from "../../../utils/useAxios";
import { useMetaArtworkSearchStore } from "../../../context/MetaArtworkSearchContext";
import { artworkMasterDataGlobalPermissions } from "../helpers/artworkPermissions";
import AuthContext from "../../../context/AuthContext";

type StatetType = {
  collection: boolean;
  metaArtwork: boolean;
  printingTechnique: boolean;
};

type ArtworkTableProp = {
  localStoragePrefix: TablePagePersistentTypes["prefix"];
  checkUnsaved?: boolean;
  showCustomFields?: Array<string>;
  filters?: {
    code?: string;
    queryString?: string;
  };
  enableAdvancedFilters?: boolean;
  showClearButton?: boolean;
  statet?: StatetType;
};

const ArtworkTable = memo(
  ({
    localStoragePrefix,
    filters,
    showCustomFields,
    enableAdvancedFilters = true,
    showClearButton = true,
    statet,
  }: ArtworkTableProp) => {
    const { state, dispatch } = useMetaArtworkSearchStore();

    const [pageSize, setPageSize] = useState(() => {
      return retrieveValueFromLocalStorageObject<TablePagePersistentTypes>(
        localStoragePrefix,
        "lastTableTopFilters",
        "pageSize",
        50,
      );
    });
    const gridRef = useRef<AgGridReact>(null);
    const [search, setSearch] = useState(() => {
      return retrieveValueFromLocalStorageObject<TablePagePersistentTypes>(
        localStoragePrefix,
        "lastTableTopFilters",
        "search",
        "",
      );
    });
    const [searchToBeSent, setSearchToBeSent] = useState<string>(() => {
      return retrieveValueFromLocalStorageObject<TablePagePersistentTypes>(
        localStoragePrefix,
        "lastTableTopFilters",
        "search",
        "",
      );
    });

    const axios = useAxios();

    const [artworkNumberImg, setArtworkNumberImg] = useState<any>({
      name: "",
      image: "",
    });
    const [artworkPrintingTechniqueImg, setArtworkPrintingTechniqueImg] =
      useState<any>();
    const [showPictureModal, setShowPictureModal] = useState(false);
    const [isModalLoading, setIsModalLoading] = useState(false);

    const closeModal = () => {
      setShowPictureModal(false);
    };

    const getArtworkNumberImage = async (title: string, fileName: string) => {
      if (fileName) {
        setIsModalLoading(true);
        const response = await axios.get(
          `/artwork-master/meta-artwork/number-upload-image/?image_file_name=${fileName}`,
        );

        setArtworkNumberImg(({ ...prev }) => {
          return { name: title, image: response.data };
        });
        setIsModalLoading(false);
      }
      return;
    };

    const { debounce } = useContext<DebounceContextType>(DebounceContext);

    const authContext = useContext(AuthContext);
    const hasApproverPermission = artworkMasterDataGlobalPermissions.every(
      (permission) => authContext?.hasPermission(permission),
    );

    const {
      state: { offset, isFirstRender },
      dispatch: dispatchPagableTable,
    } = usePageableTable();

    const CONTEXT_MAPPING = {
      2: { label: "Collection", path: "collections" },
      // 43: { label: "Meta Artwork Name", path: "artworks/name" },
      44: { label: "Meta Artwork Number", path: "meta-artwork" },
      45: { label: "Artwork", path: "artwork/" },
    };

    const defaultColDef = useMemo(
      () => ({
        sortingOrder: ["asc" as const, "desc" as const],
        minWidth: 100,
        flex: 1,
        wrapHeaderText: true,
        autoHeaderHeight: true,
        wrapText: true,
        autoHeight: true,
        suppressMenu: true,
        suppressMovable: true,
        icons: {
          sortAscending: "<i class='fa fa-sort-up'/>",
          sortDescending: "<i class='fa fa-sort-down'/>",
          sortUnSort: "<i class='fa fa-sort' style='color:#e3e6f0'></i>",
          filter: "<i class='fa fa-filter'></i>",
        },
      }),
      [],
    );

    const ArtworkApprovalRequestsIdCellRenderer = memo(
      (model: { id: number | string }) => {
        return (
          <div className="d-flex">
            <span className="pe-2 flex-fill align-self-center">{model.id}</span>
            <div></div>
          </div>
        );
      },
    );

    const columnDefs = useMemo(
      () => [
        {
          colId: "code",
          headerName: "Code",
          field: "code",
          minWidth: 70,
          maxWidth: 150,
          flex: 3,
        },
        {
          colId: "name",
          headerName: "Name",
          field: "name",
          minWidth: 100,
          flex: 2,
        },
        {
          colId: "licensor",
          headerName: "Licensor",
          valueGetter: (params: any) => params.data?.licensor?.name ?? "",
          //field: "licensor",
          minWidth: 70,
          maxWidth: 200,
        },
        {
          colId: "license ",
          headerName: "License",
          valueGetter: (params: any) =>
            params.data?.licensed_property?.name ?? "",
          //field: "license ",
          minWidth: 70,
          maxWidth: 200,
        },
        {
          colId: "launch_date",
          headerName: "Launch Date",
          field: "launch_date",
          minWidth: 70,
          maxWidth: 200,
        },
        {
          colId: "requestor_location",
          headerName: "Requestor Location",
          field: "requestor_location",
          minWidth: 70,
          maxWidth: 100,
          cellRenderer: (params: any) => {
            const requestHistory = params.data.request_history;
            if(requestHistory?.length>0){
              return <div>{requestHistory[requestHistory?.length - 1]?.user.location.name}</div>;
            }
            return <></>
          },
        },
        {
          colId: "global_product",
          headerName: "Global Products Name - Code",
          cellRenderer: (params: any) => {
            const gPList = params.data?.global_product || [];

            return (
              <ul className="list-group list-group-flush gap-2">
                {gPList.map((globalProduct: any) => (
                  <li>{`${globalProduct?.sub_category?.parent?.code}${globalProduct?.sub_category.code}${globalProduct?.product_line.code}${globalProduct?.suffix} - ${globalProduct.name}`}</li>
                ))}
              </ul>
            );
          },
          field: "global_product",
          minWidth: 100,
          maxWidth: 300,
        },
        // TODO: VALIDATE THAT COLUMN BECAUSE HERE STATUS DOES NOT MAKE ANY SENSE, IT WILL ALWAYS BE APPROVED
        // {
        //   colId: "line_planning_status",
        //   field: "data.status",
        //   headerName: `Status`,
        //   minWidth: 70,
        //   maxWidth: 100,
        //   cellRenderer: (params: any) => {
        //     return <ArtworkApprovalRequestsIdCellRenderer id={params?.data?.status} />;
        //   },
        // },
        {
          headerName: "Actions",
          minWidth: 70,
          maxWidth: 250,
          cellRenderer: (params: any) => {
            return (
              <>
                <Link
                  className="mt-1 me-3"
                  to={
                    // @ts-ignore
                    `/artwork-master/${CONTEXT_MAPPING[2].path}/detail/${params?.data?.code}`
                  }
                  style={{ textDecoration: "underline" }}
                >
                  View
                </Link>

                {hasApproverPermission && (
                  <Link
                    className="mt-1 me-3"
                    to={
                      // @ts-ignore
                      `/artwork-master/${CONTEXT_MAPPING[2].path}/detail/${params?.data?.code}?editing=true`
                    }
                    style={{ textDecoration: "underline" }}
                  >
                    Edit
                  </Link>
                )}
              </>
            );
          },
        },
      ],
      [],
    );

    const columnDefsNumber = useMemo(
      () => [
        {
          colId: "code",
          headerName: "Meta Artwork Code",
          field: "artwork_name.code",
          maxWidth: 100,
          flex: 3,
        },
        {
          colId: "name",
          headerName: "Meta Artwork Name",
          field: "artwork_name.name",
          minWidth: 100,
          maxWidth: 250,
          flex: 2,
        },
        {
          // colId: "name",
          headerName: "Meta Artwork Number",
          field: "code",
          minWidth: 150,
          maxWidth: 250,
          flex: 2,
        },
        {
          //colId: "decoration_technique",
          headerName: "Collection Code - Name",
          valueGetter: (params: any) =>
            `${params.data?.collection?.code} - ${params.data?.collection?.name}` ??
            "",
          field: "collection.name",
          minWidth: 150,
          maxWidth: 350,
        },
        {
          colId: "artwork_launch_date",
          headerName: "Meta Artwork Launch Date",
          valueGetter: (params: any) =>
            params.data?.artwork_name?.launch_date?.split(" ")[0] ?? "",
          minWidth: 100,
          // maxWidth: 200,
          // cellRenderer: (params: any) => (
          //   <ArtworkApprovalRequestsIdCellRenderer id={DateTimeFormat.formatDate(params?.data?.artwork_name?.launch_date)} />
          // ),
        },
        {
          colId: "collection_launch_date",
          headerName: "Collection Launch Date",
          valueGetter: (params: any) =>
            params.data?.collection?.launch_date ?? "",
          minWidth: 100,
          // maxWidth: 200,
          // cellRenderer: (params: any) => (
          //   <ArtworkApprovalRequestsIdCellRenderer id={params?.data?.status} />
          // ),
        },{
          colId: "requestor_location",
          headerName: "Requestor Location",
          field: "requestor_location",
          minWidth: 70,
          maxWidth: 100,
          cellRenderer: (params: any) => {
            const requestHistory = params.data.request_history;
            if(requestHistory?.length>0){
              return <div>{requestHistory[requestHistory?.length - 1]?.user.location.name}</div>;
            }
            return <></>
          },
        },
        {
          headerName: "Actions",
          minWidth: 70,
          maxWidth: 250,
          cellRenderer: (params: any) => {
            return (
              <>
                <Link
                  className="mt-1 me-3"
                  to={
                    // @ts-ignore
                    `/artwork-master/${CONTEXT_MAPPING[44].path}/detail/${params?.data?.code}`
                  }
                  style={{ textDecoration: "underline" }}
                >
                  View
                </Link>

                {hasApproverPermission && (
                  <Link
                    className="mt-1 me-3"
                    to={
                      // @ts-ignore
                      `/artwork-master/${CONTEXT_MAPPING[44].path}/detail/${params?.data?.code}?editing=true`
                    }
                    style={{ textDecoration: "underline" }}
                  >
                    Edit
                  </Link>
                )}

                {params.data.file_uploaded && (
                  <Link
                    to=""
                    className="mt-1 me-3 text-nowrap"
                    style={{ textDecoration: "underline", cursor: "pointer" }}
                    onClick={() => {
                      setShowPictureModal(true);
                      getArtworkNumberImage(
                        `Artwork number ${params.data.code}`,
                        params.data.file_uploaded,
                      );
                    }}
                  >
                    Artwork File
                  </Link>
                )}
              </>
            );
          },
        },
      ],
      [],
    );

    const columnDefsPrintingTechnique = useMemo(
      () => [
        {
          colId: "id",
          headerName: "Code",
          minWidth: 75,
          maxWidth: 100,
          flex: 3,
          cellRenderer: (params: any) => {
            return (
              `${params.data?.decoration_technique?.id.toString().padStart(2, 0)}-${params.data?.id.toString().padStart(5, 0)}` ??
              ""
            );
          },
        },
        {
          colId: "name",
          headerName: "Name",
          field: "name",
          minWidth: 175,
          maxWidth: 300,
          flex: 2,
        },
        {
          colId: "decoration_technique",
          headerName: "Decoration Technique",
          cellRenderer: (params: any) => !!params.data?.decoration_technique?.id ?
            `${params.data?.decoration_technique?.id} - ${params.data?.decoration_technique?.name}` : "",
          //field: "licensor",
          maxWidth: 150,
        },
        {
          colId: "color",
          headerName: "Color",
          cellRenderer: (params: any) => !!params.data?.color?.id ?`${params.data?.color?.id} - ${params.data?.color?.name}` : "",
          //field: "license ",
          minWidth: 100,
          maxWidth: 120,
          // cellRenderer:  (params: any) => `${params.data?.color?.id}- ${params.data?.color?.name}` ?? "",
        },
        {
          colId: "molds",
          headerName: "Molds",
          //valueGetter: (params: any) => params.data?.color?.name ?? "",
          minWidth: 120,
          maxWidth: 200,
          cellRenderer: (params: any) => {
            return params.data?.molds?.map((mold: any) => {
              return (
                <p key={mold.id}>
                  {mold.id} - {mold.name}
                </p>
              );
            });
          },
        },
        {
          colId: "collection",
          headerName: "Collection Code/Name",
          valueGetter: (params: any) =>
            `${params.data?.collection?.code} - ${params.data?.collection?.name}` ??
            "",
          minWidth: 100,
          // maxWidth: 120,
        },
        {
          colId: "artwork_name",
          headerName: "Meta Artwork Name",
          field: "artwork_name",
          minWidth: 100,
          // maxWidth: 120,
          cellRenderer: (params: any) => {
            return (
              `${params.data?.artwork_name?.code} - ${params.data?.artwork_name?.name}` ??
              ""
            );
          },
        },
        {
          colId: "artwork_number",
          headerName: "Meta Artwork Number",
          field: "artwork_umber",
          minWidth: 100,
          // maxWidth: 120,
          cellRenderer: (params: any) => {
            return `${params.data?.artwork_number?.code}` ?? "";
          },
        },
        {
          colId: "launch_date",
          headerName: "Meta Artwork Name Launch Date",
          valueGetter: (params: any) =>
            params.data?.artwork_name?.launch_date?.split(" ")[0] ?? "",
          minWidth: 100,
          maxWidth: 200,
          // cellRenderer: (params: any) => (
          //   <ArtworkApprovalRequestsIdCellRenderer id={params?.data?.status} />
          // ),
        },
        {
          colId: "requestor_location",
          headerName: "Requestor Location",
          field: "requestor_location",
          minWidth: 70,
          maxWidth: 100,
          cellRenderer: (params: any) => {
            const requestHistory = params.data.request_history;
            if(requestHistory?.length>0){
              return <div>{requestHistory[requestHistory?.length - 1]?.user.location.name}</div>;
            }
            return <></>
          },
        },
        {
          headerName: "Actions",
          minWidth: 70,
          maxWidth: 250,
          cellRenderer: (params: any) => {
            return (
              <>
                <Link
                  className="mt-1 me-3"
                  to={
                    // @ts-ignore
                    `/artwork-master/${CONTEXT_MAPPING[45].path}/detail/${params?.data?.unique_id}`
                  }
                  style={{ textDecoration: "underline" }}
                >
                  View
                </Link>
                {hasApproverPermission && (
                  <Link
                    className="mt-1 me-3"
                    to={
                      // @ts-ignore
                      `/artwork-master/${CONTEXT_MAPPING[45].path}/detail/${params?.data?.unique_id}?editing=true`
                    }
                    style={{ textDecoration: "underline" }}
                  >
                    Edit
                  </Link>
                )}

                {params.data.artwork_number?.file_uploaded && (
                  <Link
                    to=""
                    className="mt-1 me-3 text-nowrap"
                    style={{ textDecoration: "underline", cursor: "pointer" }}
                    onClick={() => {
                      setShowPictureModal(true);
                      getArtworkNumberImage(
                        `Artwork number ${params.data.artwork_number?.code}`,
                        params.data.artwork_number?.file_uploaded,
                      );
                    }}
                  >
                    Artwork File
                  </Link>
                )}

                {params.data.picture && (
                  <Link
                    to=""
                    className="mt-1 me-3 text-nowrap"
                    style={{ textDecoration: "underline", cursor: "pointer" }}
                    onClick={() => {
                      setShowPictureModal(true);
                      getArtworkNumberImage(
                        `Artwork ${params.data?.decoration_technique?.id.toString().padStart(2, 0)}-${params.data?.id.toString().padStart(5, 0)}`,
                        params.data.picture,
                      );
                    }}
                  >
                    Technical File
                  </Link>
                )}
              </>
            );
          },
        },
      ],
      [],
    );

    const { data: artworks, isLoading: isLoading } = useArtworkSearch({
      limit: pageSize,
      offset,
      queryString: filters?.queryString,
      search: Predicates.isNotNullAndNotUndefinedAndNotEmpty(showCustomFields)
        ? searchToBeSent
        : undefined,
    });

    const tableProp = useMemo(
      () => ({
        gridRef,
        // rowData: artworks?.data ?? [],
        //@ts-ignore
        rowData: state.responseDataCollection?.data ?? [],
        columnDefs,
        defaultColDef,
      }),
      [gridRef, state.responseDataCollection],
    );

    const tablePropArtworkNumber = useMemo(
      () => ({
        gridRef,
        // rowData: artworks?.data ?? [],
        //@ts-ignore
        rowData: state.responseDataMetaArtwork?.data ?? [],
        columnDefs: columnDefsNumber,
        defaultColDef,
      }),
      [gridRef, state.responseDataMetaArtwork],
    );

    const tablePropPrintingTechnique = useMemo(
      () => ({
        gridRef,
        // rowData: artworks?.data ?? [],
        //@ts-ignore
        rowData: state.responseDataPrintingTechnique?.data ?? [],
        columnDefs: columnDefsPrintingTechnique,
        defaultColDef,
      }),
      [gridRef, state.responseDataPrintingTechnique],
    );

    const onPageSizeChanged = useCallback((e: any) => {
      setPageSize(Number(e.target.value));
    }, []);

    const handleTableSearch = (elem: any) => {
      setSearch(elem.target.value.toLowerCase().trimStart());
      debounce(setSearchToBeSent, 500)(elem?.target?.value ?? "");
      saveObjectsToLocalStorage<TablePagePersistentTypes>({
        prefix: localStoragePrefix,
        lastTableTopFilters: {
          search: elem.target.value.toLowerCase().trimStart(),
          pageSize: pageSize,
        },
      });
    };

    const clearAllFilters = () => {
      setSearch("");
      setSearchToBeSent("");
      if (Predicates.isNotNullAndNotUndefined(gridRef.current)) {
        gridRef.current.api?.setFilterModel(null);
      }
      saveObjectsToLocalStorage<TablePagePersistentTypes>({
        prefix: localStoragePrefix,
        lastTableFilters: [],
        lastTableTopFilters: {},
      });
    };

    useEffect(() => {
      dispatchPagableTable(PageableTableActions.setOffset(0));
    }, [filters?.queryString, dispatchPagableTable]);

    useEffect(() => {
      dispatchPagableTable(PageableTableActions.setPageSize(pageSize));
    }, [pageSize, dispatchPagableTable]);

    useEffect(() => {
      saveObjectsToLocalStorage<TablePagePersistentTypes>({
        prefix: localStoragePrefix,
        lastTableTopFilters: {
          search: search,
          pageSize: Number(pageSize),
        },
      });
    }, [pageSize, dispatchPagableTable, localStoragePrefix, search]);

    useEffect(() => {
      if (!isLoading && Predicates.isNotNullAndNotUndefined(artworks))
        dispatchPagableTable(
          PageableTableActions.setTotal(
            artworks.data.length > 0 ? artworks?.total : 0,
          ),
        );
    }, [isLoading, artworks, dispatchPagableTable]);

    useEffect(() => {
      if (!isFirstRender) {
        dispatchPagableTable(PageableTableActions.setLoading(isLoading));
      }
    }, [isLoading, dispatchPagableTable, artworks]);

    useEffect(() => {
      if (
        Predicates.isNotNullAndNotUndefinedAndNotEmpty(filters?.queryString) &&
        isFirstRender
      ) {
        dispatchPagableTable(PageableTableActions.setFirstRender(false));
      }
    }, [filters, isFirstRender, dispatchPagableTable]);

    return (
      <>
        {enableAdvancedFilters ? (
          <div className="d-flex justify-content-between table-top-container">
            <div className="d-flex align-items-center">
              <label className="d-inline-block">
                Show
                <select
                  onChange={onPageSizeChanged}
                  className="table-top-input"
                  id="page-size"
                  value={pageSize}
                >
                  <option value="50">50</option>
                  <option value="100">100</option>
                  <option value="200">200</option>
                  <option value="500">All</option>
                </select>
                entries
              </label>
            </div>

            <div className="d-flex justify-content-center align-items-center">
              <div
                id="pp_search_filter"
                className="dataTables_filter table-top-search"
              >
                <label>
                  Search:
                  <input
                    onChange={handleTableSearch}
                    type="search"
                    className="table-top-input"
                    placeholder=""
                    aria-controls="pp_search"
                    value={search}
                  />
                </label>
              </div>

              {showClearButton ? (
                <button
                  className="icon-button ml-3 mr-3"
                  onClick={clearAllFilters}
                  disabled={Predicates.isNullOrUndefinedOrEmpty(search)}
                >
                  <FontAwesomeIcon size="lg" icon={faFilterCircleXmark} />
                </button>
              ) : (
                <></>
              )}
            </div>
          </div>
        ) : (
          <></>
        )}
        {state.artworkLevel === "collection" && (
          <PageableTable tableProp={tableProp} />
        )}
        {state.artworkLevel === "meta-artwork" && (
          <PageableTable tableProp={tablePropArtworkNumber} />
        )}
        {state.artworkLevel === "printing-technique" && (
          <PageableTable tableProp={tablePropPrintingTechnique} />
        )}

        <ReactModal
          isOpen={showPictureModal}
          className="custom-modal mold-comps"
          overlayClassName="custom-overlay"
        >
          <ArtworkPictureModal
            handleCloseModal={closeModal}
            product={{
              // Artwork Number ${state.code}
              imageName: artworkNumberImg.name,
              image_file: `data:image/jpeg;base64,${artworkNumberImg.image}`,
            }}
            isLoading={isModalLoading}
          />
        </ReactModal>
      </>
    );
  },
);

export default ArtworkTable;
